import { X } from 'lucide-react';
import { useEffect, useRef, useState, type FC, type MutableRefObject } from 'react';

import { geocode, type GeocodeResult } from '@/lib/routing';
import type { GpsPosition } from '@/types/geo';

// Type-ahead cadence: fast enough to feel live, slow enough not to hammer Photon.
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_MIN_CHARS = 2;

// Moved to lib/routing (pure string helper needed outside components too); re-exported
// here so the existing component-side imports keep working.
export { shortPlaceName } from '@/lib/routing';

/**
 * Search-as-you-type place lookup (Photon, via the backend proxy): results drop down
 * under the input as you type, debounced, with stale requests aborted. Selecting a hit
 * collapses the input into a removable pill. Extracted from RoutePlannerPage for the M3
 * route editor's choosable start (and M4 discover).
 */
const PlaceSearch: FC<{
  placeholder: string;
  selected: GeocodeResult | null;
  onSelect: (place: GeocodeResult | null) => void;
  /** Prefill the dropdown while the field is focused but still empty; typing replaces them with results. */
  recent?: GeocodeResult[];
  /** Rider's latest fix (ref — read at search time) to rank nearby places first. */
  biasRef?: MutableRefObject<GpsPosition | null>;
}> = ({ placeholder, selected, onSelect, recent, biasRef }) => {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [results, setResults] = useState<GeocodeResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Picking a result resets the field: parents that keep `selected` null (discover, the
  // editor) reuse it for the next search, so the text clears, the dropdown closes and the
  // keyboard dismisses (blur) — the map is what should have the rider's attention now.
  // Parents that show the pill unmount the input anyway, so the reset is harmless there.
  const pick = (place: GeocodeResult) => {
    setQuery('');
    setResults(null);
    inputRef.current?.blur();
    onSelect(place);
  };

  useEffect(() => {
    const q = query.trim();
    if (q.length < SEARCH_MIN_CHARS) {
      setResults(null);
      setError(null);
      return;
    }
    const id = setTimeout(() => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      const pos = biasRef?.current;
      geocode(q, pos ? { lat: pos.lat, lon: pos.lon } : undefined, ctrl.signal)
        .then((places) => {
          setResults(places);
          setError(null);
        })
        .catch((e) => {
          if (ctrl.signal.aborted) return; // superseded by a newer keystroke
          setError(e instanceof Error ? e.message : 'Search failed');
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query, biasRef]);

  // Abort any in-flight lookup when the field unmounts (sheet closed, pill selected).
  useEffect(() => () => abortRef.current?.abort(), []);

  if (selected) {
    return (
      <div className="mg-place-pill">
        <span className="mg-place-pill-name">{selected.name}</span>
        <button className="mg-icon-btn" onClick={() => onSelect(null)} title="Clear" aria-label="Clear selection">
          <X size={20} />
        </button>
      </div>
    );
  }

  // Dropdown only while the field is focused: recents prefill it when empty, typed
  // results replace them. preventDefault on mousedown keeps a tap on an entry from
  // blurring the input before its click lands.
  const showRecents = focused && query.trim() === '' && !!recent && recent.length > 0;
  const dropdown = focused ? (results ?? (showRecents ? recent : null)) : null;

  return (
    <div className="mg-place-search">
      <input
        ref={inputRef}
        className="mg-input"
        type="text"
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
      {error && <div className="mg-plan-error">{error}</div>}
      {focused && results && results.length === 0 && <div className="mg-plan-hint">No places found.</div>}
      {dropdown && dropdown.length > 0 && (
        <ul className="mg-place-results" onMouseDown={(e) => e.preventDefault()}>
          {dropdown.map((place, i) => (
            <li key={i}>
              <button className="mg-place-result" onClick={() => pick(place)}>
                {place.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PlaceSearch;
